import { Components } from '@mui/material/styles'
import { TypographyOptions } from '@mui/material/styles/createTypography'
import type {} from '@mui/lab/themeAugmentation'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/inter/800.css'
import '@fontsource/inter/900.css'
import React from 'react'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import type {} from '@mui/x-date-pickers-pro/themeAugmentation'
import { PaletteMode } from '@mui/material'
import { COLORS } from 'app-constants'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    pXS: React.CSSProperties
    pS: React.CSSProperties
    pSB: React.CSSProperties
    pSO: React.CSSProperties
    pL: React.CSSProperties
    pXL: React.CSSProperties
    'h4.5': React.CSSProperties
    'h4.75': React.CSSProperties
    'h5.5': React.CSSProperties
    h7: React.CSSProperties
    h8: React.CSSProperties
    'h8.75': React.CSSProperties
  }

  interface TypographyVariantsOptions {
    pXS?: React.CSSProperties
    pS?: React.CSSProperties
    pSM?: React.CSSProperties
    pSO?: React.CSSProperties
    pSB?: React.CSSProperties
    pL?: React.CSSProperties
    pXL?: React.CSSProperties
    h7?: React.CSSProperties
    h8?: React.CSSProperties
    'h1.5'?: React.CSSProperties
    'h2.5'?: React.CSSProperties
    'h3.5'?: React.CSSProperties
    'h3.75'?: React.CSSProperties
    'h4.5'?: React.CSSProperties
    'h4.75': React.CSSProperties
    'h5.5'?: React.CSSProperties
    'h7.25'?: React.CSSProperties
    'h7.5'?: React.CSSProperties
    'h8.5'?: React.CSSProperties
    'h8.75'?: React.CSSProperties
    pLW?: React.CSSProperties
    body0?: React.CSSProperties
    body1W?: React.CSSProperties
    body2W?: React.CSSProperties
    body2?: React.CSSProperties
  }

  interface Palette {
    lightGrey: Palette['primary']
    white: Palette['primary']
  }

  interface PaletteOptions {
    lightGrey: PaletteOptions['primary']
    white: PaletteOptions['primary']
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pXS: true
    pS: true
    pSO: true
    pSM: true
    pSB: true
    pL: true
    pXL: true
    h7: true
    h8: true
    'h1.5': true
    'h2.5': true
    'h3.5': true
    'h3.75': true
    'h4.5': true
    'h4.75': true
    'h5.5': true
    'h7.25': true
    'h7.5': true
    'h8.5': true
    'h8.75': true
    pLW: true
    body0: true
    body1W: true
    body2W: true
    body2: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    lightGrey: true
    white: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    white: true
  }
}

export const typography: TypographyOptions = {
  fontFamily: ['Inter'].join(','),
  h1: {
    fontSize: '48px',
    fontWeight: 800,
    lineHeight: 'normal',
    letterSpacing: 0,
  },
  'h1.5': {
    fontSize: '40px',
    fontWeight: 800,
    lineHeight: '48px',
    letterSpacing: '-0.03em',
  },
  h2: {
    fontSize: '32px',
    fontWeight: 900,
    lineHeight: '40px',
    letterSpacing: 0,
  },
  'h2.5': {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '40px',
    letterSpacing: 0,
  },
  h3: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
    letterSpacing: 0,
  },
  'h3.5': {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
    letterSpacing: 0,
  },
  'h3.75': {
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '32px',
    letterSpacing: 0,
  },
  h4: {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '28px',
    letterSpacing: 0,
  },
  'h4.5': {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '28px',
    letterSpacing: 0,
  },
  'h4.75': {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: 0,
  },
  h5: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: 0,
  },
  'h5.5': {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: 0,
  },
  h6: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  h7: {
    fontSize: '12px',
    fontWeight: 800,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  'h7.25': {
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  'h7.5': {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  h8: {
    fontSize: '10px',
    fontWeight: 800,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  'h8.5': {
    fontSize: '10px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  'h8.75': {
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  pXS: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  pS: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  pSO: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  pSM: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  pSB: {
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  body0: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  // body1 = pM in figma
  body1: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: 0,
  },
  body2: {
    fontSize: '24px',
    fontWeight: 400,
    lineHeight: '40px',
    letterSpacing: 0,
  },
  pL: {
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '28px',
    letterSpacing: 0,
  },
  pLW: {
    fontSize: '20px',
    fontWeight: 300,
    lineHeight: '44px',
    letterSpacing: 0,
  },
  body1W: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
  },
  body2W: {
    fontWeight: 400,
    fontSize: '20px',
    lineHeight: '40px',
  },
  pXL: {
    fontSize: '32px',
    fontWeight: 400,
    lineHeight: '40px',
    letterSpacing: 0,
  },
}

export const components: Components = {
  MuiCalendarPicker: {
    styleOverrides: {
      root: {
        '.PrivatePickersFadeTransitionGroup-root': {
          fontWeight: 700,
          '.MuiPickersDay-root': {
            fontWeight: 600,
          },
          '.MuiPickersDay-root.Mui-selected': {
            backgroundColor: COLORS.GRAY_0,
          },
          '.MuiPickersDay-root:not(.Mui-selected)': {
            border: 'none',
            backgroundColor: 'white',
          },
        },
      },
    },
  },
  MuiPickerStaticWrapper: {
    styleOverrides: {
      root: {
        padding: 16,
        '.MuiTypography-subtitle1': {
          fontWeight: 700,
        },
        '.MuiPickersDay-root.MuiDateRangePickerDay-day': {
          fontWeight: 600,
        },
        '.MuiPickersDay-root.Mui-selected.MuiDateRangePickerDay-day': {
          backgroundColor: COLORS.GRAY_0,
        },
        '.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
          backgroundColor: COLORS.GRAY_5,
        },
        '.PrivatePickersSlideTransition-root': {
          minHeight: 'auto',
          height: '264px',
        },
        '.MuiTypography-caption': {
          fontWeight: 600,
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      InputLabelProps: {
        shrink: true,
      },
    },
    styleOverrides: {
      root: {
        borderRadius: '8px',
        '& .MuiOutlinedInput-root': {
          'legend span': {
            display: 'block',
          },
        },
        '&.has-label .MuiOutlinedInput-root': {
          'legend span': {
            display: 'none', // Hide the legend when the label is present
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    defaultProps: {
      notched: true,
    },
    styleOverrides: {
      root: {
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        paddingRight: 0,
        paddingLeft: '8px',
        color: COLORS.GRAY_0,
        backgroundColor: COLORS.PURE_WHITE,

        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: COLORS.GRAY_5,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: COLORS.JELLED_PRIMARY_BLUE,
        },
      },
      notchedOutline: {
        border: '1px solid #DDDDDD',
        // There doesn't seem to be another way to remove the 2px focus border other than by using
        // !important here. Adding the `focused` override does not apply at render time.
      },
      input: {
        padding: '8px',
      },
      multiline: {
        padding: 0,
      },
      inputSizeSmall: {
        padding: '8px 8px',
      },
    },
  },
  MuiInputLabel: {
    defaultProps: { shrink: true },
    styleOverrides: {
      root: {
        top: '-12px',
        color: COLORS.GRAY_2,
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        color: COLORS.GRAY_2,
        marginLeft: '-14px',
        '&.Mui-focused': {
          color: COLORS.GRAY_1,
        },
      },
      asterisk: {
        color: COLORS.RED,
        marginLeft: '-2px',
        '&$error': {
          color: COLORS.RED,
        },
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        pXS: 'p',
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '8px',
      },
      text: {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 400,
      },
      textSizeSmall: {
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '16px',
      },
      textSizeLarge: {
        fontSize: '16px',
        lineHeight: '24px',
        padding: '16px 32px',
        '&:active': {
          textDecoration: 'underline',
        },
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      containedSizeSmall: {
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        padding: '8px 16px',
      },
      containedSizeMedium: {
        fontSize: '14px',
        lineHeight: '20px',
        padding: '12px 40px',
      },
      containedSizeLarge: {
        fontSize: '16px',
        lineHeight: '24px',
        padding: '16px 32px',
      },
      contained: {
        color: 'white',

        '&:disabled': {
          background: COLORS.GRAY_4,
          color: COLORS.PURE_WHITE,
        },
      },
      containedPrimary: {
        '&:hover': {
          background: COLORS.JELLED_LIGHT_BLUE,
        },
      },
      containedSecondary: {
        '&:hover': {
          background: COLORS.GRAY_2,
        },
      },
      outlined: {
        borderWidth: '1px',
        '&:hover': {
          borderWidth: '1px',
        },
      },
      outlinedSecondary: {
        borderColor: COLORS.GRAY_5,
        '&:hover': {
          background: COLORS.GRAY_6,
        },
      },
      outlinedSizeSmall: {
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        padding: '7px 16px', // 2px less v-padding to adjust for the css border
      },
      outlinedSizeMedium: {
        fontSize: '14px',
        lineHeight: '20px',
        padding: '8px 26px', // 2px less v-padding to adjust for the css border
        boxSizing: 'border-box',
        border: `1px solid ${COLORS.GRAY_5}`,
        '&:hover': {
          background: COLORS.GRAY_8,
          border: `1px solid ${COLORS.GRAY_5}`,
        },
      },
      outlinedSizeLarge: {
        fontSize: '16px',
        lineHeight: '24px',
        padding: '14px 32px', // 2px less v-padding to adjust for the css border
        boxSizing: 'border-box',
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      color: 'secondary',
    },
  },
  MuiCircularProgress: {
    defaultProps: {
      thickness: 5,
    },
    styleOverrides: {
      root: {
        color: 'white',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: {
        zIndex: 1503, // needs to be above MuiModal
      },
      paper: {
        borderRadius: '16px',
        width: '100%',
        backgroundColor: COLORS.PURE_WHITE,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        color: COLORS.GRAY_0,
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '24px',
        letterSpacing: 0,
        padding: '32px 32px 0',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '0 32px',
      },
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: {
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '20px',
        letterSpacing: 0,
        color: COLORS.GRAY_2,
      },
    },
  },
  MuiDialogActions: {
    defaultProps: {
      disableSpacing: true,
    },
    styleOverrides: {
      root: {
        padding: '32px',
        gap: '16px',
      },
    },
  },
  MuiSnackbar: {
    defaultProps: {
      anchorOrigin: {
        horizontal: 'center',
        vertical: 'bottom',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        background: COLORS.GRAY_0,
        color: COLORS.PURE_WHITE,
        padding: '14px 16px',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '24px',
        letterSpacing: 0,
        borderRadius: '8px',
      },
      icon: {
        padding: 0,
        marginRight: '8px',
      },
      message: {
        padding: 0,
      },
    },
    defaultProps: {
      iconMapping: {
        success: React.createElement(CheckIcon),
        error: React.createElement(CloseIcon),
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      rectangular: {
        borderRadius: '8px',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: '22px',
      },
      flexContainer: {
        gap: '32px',
      },
      indicator: {
        borderRadius: '9999px',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        padding: 0,
        minWidth: 0,
        minHeight: 0,
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '20px',
        letterSpacing: 0,
        color: COLORS.GRAY_2,

        '&.Mui-selected': {
          color: COLORS.GRAY_0,
        },

        '.MuiTab-iconWrapper': {
          // Small hack to get the selection bar to only take with with of the label
          marginLeft: '4px',
        },
      },
      labelIcon: {
        // Small hack to get the selection bar to only take with with of the label
        overflow: 'visible',
        marginRight: '28px',
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      colorSecondary: {
        color: COLORS.GRAY_0,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '16px',
        letterSpacing: 0,
        color: COLORS.RED,
        paddingTop: '8px',
        marginLeft: 0,
        marginRight: 0,
        backgroundColor: COLORS.PURE_WHITE,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      root: {
        zIndex: 1503, // needs to be above modals
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.14px',
        minWidth: '240px',
        '&.Mui-selected': {
          backgroundColor: COLORS.PURE_WHITE,
          color: COLORS.GRAY_0,
        },
        '&.Mui-selected:hover': {
          color: COLORS.GRAY_0,
        },
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        listStyleType: 'disc',
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '24px',
        letterSpacing: 0,
        display: 'list-item',
        paddingLeft: 0,
        color: COLORS.GRAY_2,
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      root: {
        color: COLORS.GRAY_0,
      },
    },
    defaultProps: {
      disableTypography: true,
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: COLORS.GRAY_0,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.2)',
        borderRadius: '8px',
        overflow: 'hidden',
      },
    },
  },
  MuiPopper: {
    defaultProps: {
      sx: { zIndex: 1499 },
    },
  },
  MuiPopover: {
    defaultProps: {
      sx: { zIndex: 1500 }, // needs to be at the same zindex as MuiModal
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: '9999px',
        background: COLORS.GRAY_5,
      },
      bar: {
        background: COLORS.GRAY_5,
      },
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: {
        color: COLORS.JELLED_PRIMARY_BLUE,
        height: '16px',
        borderRadius: '4px',
      },
      track: {
        border: 'none',
      },
      rail: {
        background: COLORS.GRAY_6,
        opacity: 1,
      },
      thumb: {
        height: 20,
        width: 20,
        background: COLORS.JELLED_PRIMARY_BLUE,
        border: '4px solid white',
        boxShadow:
          '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
        '&:before': {
          display: 'none',
        },
      },
      valueLabel: {
        background: COLORS.GRAY_0,
        borderRadius: '4px',
        fontWeight: 800,
        fontSize: '12px',
        lineHeight: '16px',
        padding: '8px 12px',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: COLORS.JELLED_PRIMARY_BLUE,
        textDecoration: 'none',
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        background: COLORS.GRAY_0,
        padding: '16px',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '16px',
      },
      arrow: {
        color: COLORS.GRAY_0,
      },
    },
  },
}

const getDesignTokens = (mode: PaletteMode) => ({
  typography,
  components,
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: {
            main: COLORS.JELLED_PRIMARY_BLUE,
          },
          secondary: {
            main: COLORS.GRAY_0,
          },
          lightGrey: {
            main: COLORS.GRAY_6,
          },
          error: {
            main: COLORS.RED,
          },
          success: {
            main: COLORS.GREEN,
          },
          white: {
            main: COLORS.PURE_WHITE,
          },
        }
      : {
          primary: {
            main: COLORS.JELLED_PRIMARY_BLUE,
          },
          secondary: {
            main: COLORS.GRAY_0,
          },
          lightGrey: {
            main: COLORS.GRAY_6,
          },
          error: {
            main: COLORS.RED,
          },
          success: {
            main: COLORS.GREEN,
          },
          white: {
            main: COLORS.PURE_WHITE,
          },
        }),
  },
})

export default getDesignTokens
